import React, { useState, useEffect } from 'react';
import { Project, DeduplicationStrategy, DeduplicationScope, DeduplicatedKeywordInstance, DeduplicationState } from '../types';
import { analyzeKeywordDuplicates } from '../utils/deduplication';
import { exportDuplicatesAnalysis } from '../utils/exportDuplicates';

interface DeduplicationModalProps {
  project: Project;
  onClose: () => void;
  onApplyDeduplication: (strategy: DeduplicationStrategy, scope: DeduplicationScope) => void;
  deduplicationState: DeduplicationState | null;
  onRestoreDeduplication: () => void;
}

const DeduplicationModal: React.FC<DeduplicationModalProps> = ({ 
  project, 
  onClose, 
  onApplyDeduplication,
  deduplicationState,
  onRestoreDeduplication
}) => {
  const [strategy, setStrategy] = useState<DeduplicationStrategy>('volume');
  const [scope, setScope] = useState<DeduplicationScope>('within-groups');
  const [previewData, setPreviewData] = useState<DeduplicatedKeywordInstance[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  // Calculate preview whenever strategy or scope changes
  useEffect(() => {
    if (project.viewType) {
      const instances = analyzeKeywordDuplicates(
        project.products,
        project.viewType,
        scope,
        strategy
      );
      setPreviewData(instances);
    }
  }, [strategy, scope, project]);

  const removedCount = previewData.filter(inst => !inst.isKept).length;

  // Group instances by keyword for details view
  // When scope is "within-groups", group by keyword + groupName to show duplicates per group
  // When scope is "across-groups", group by keyword only to show duplicates across all groups
  const groupedForDetails = new Map<string, { key: string; displayName: string; groupName?: string; instances: DeduplicatedKeywordInstance[] }>();
  previewData.forEach(instance => {
    let key: string;
    let displayName: string;
    let groupName: string | undefined;
    
    if (scope === 'within-groups') {
      // Group by keyword + group name to show duplicates separately per group
      key = `${instance.keyword}|||${instance.groupName}`;
      displayName = instance.keyword;
      groupName = instance.groupName;
    } else {
      // Group by keyword only to show duplicates across all groups
      key = instance.keyword;
      displayName = instance.keyword;
      groupName = undefined;
    }
    
    if (!groupedForDetails.has(key)) {
      groupedForDetails.set(key, { key, displayName, groupName, instances: [] });
    }
    groupedForDetails.get(key)!.instances.push(instance);
  });

  // Filter to only show keywords with duplicates
  const duplicateKeywords = Array.from(groupedForDetails.values())
    .filter(group => group.instances.length > 1)
    .sort((a, b) => {
      // Sort by keyword name first, then by group name if within-groups
      const keywordCompare = a.displayName.localeCompare(b.displayName);
      if (keywordCompare !== 0) return keywordCompare;
      if (scope === 'within-groups' && a.groupName && b.groupName) {
        return a.groupName.localeCompare(b.groupName);
      }
      return 0;
    });

  const handleApply = () => {
    onApplyDeduplication(strategy, scope);
    onClose();
  };

  const handleExport = () => {
    if (project.viewType) {
      // Create a mock deduplication state for export
      const mockState = {
        isActive: true,
        strategy,
        scope,
        viewType: project.viewType,
        timestamp: new Date(),
        removedCount,
        originalProductKeywords: {},
        duplicateInstances: previewData
      };
      exportDuplicatesAnalysis(mockState, project.name);
    }
  };

  const strategyLabels = {
    'volume': 'Keep Highest Volume',
    'intent-score': 'Keep Highest Intent Score',
    'first': 'Keep First Occurrence'
  };

  const scopeLabels = {
    'within-groups': `Within groups (per ${project.viewType === 'brand' ? 'brand' : project.viewType === 'sub-brand' ? 'sub-brand' : 'product'})`,
    'across-groups': 'Across all groups (entire dataset)'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-primary-900">Deduplicate Keywords</h2>
            {deduplicationState && (
              <p className="text-sm text-purple-600 mt-1">
                {deduplicationState.removedCount} duplicate{deduplicationState.removedCount !== 1 ? 's' : ''} currently removed
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Strategy Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Strategy</h3>
          <p className="text-sm text-gray-600 mb-3">
            When duplicate keywords are found, which instance should be kept?
          </p>
          <div className="space-y-2">
            {(Object.keys(strategyLabels) as DeduplicationStrategy[]).map(key => (
              <label key={key} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value={key}
                  checked={strategy === key}
                  onChange={(e) => setStrategy(e.target.value as DeduplicationStrategy)}
                  className="w-4 h-4 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-sm text-gray-700">{strategyLabels[key]}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Scope Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Scope</h3>
          <p className="text-sm text-gray-600 mb-3">
            Should deduplication apply within each group separately, or across all groups?
          </p>
          <div className="space-y-2">
            {(Object.keys(scopeLabels) as DeduplicationScope[]).map(key => (
              <label key={key} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  value={key}
                  checked={scope === key}
                  onChange={(e) => setScope(e.target.value as DeduplicationScope)}
                  className="w-4 h-4 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-sm text-gray-700">{scopeLabels[key]}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-900">
                Preview: {removedCount} keyword{removedCount !== 1 ? 's' : ''} will be removed
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {duplicateKeywords.length} {scope === 'within-groups' ? 'group' : 'unique keyword'}{duplicateKeywords.length !== 1 ? 's' : ''} with duplicates found
              </p>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {showDetails ? 'Hide' : 'View'} Details
              <svg
                className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Details Section */}
          {showDetails && (
            <div className="mt-4 max-h-96 overflow-y-auto border-t border-blue-200 pt-4">
              <div className="space-y-4">
                {duplicateKeywords.map((group) => {
                  const keptInstance = group.instances.find(inst => inst.isKept);
                  const removedInstances = group.instances.filter(inst => !inst.isKept);

                  return (
                    <div key={group.key} className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="font-semibold text-gray-900 mb-2">
                        "{group.displayName}"
                        {scope === 'within-groups' && group.groupName && (
                          <span className="text-sm font-normal text-gray-600 ml-2">
                            in {project.viewType === 'brand' ? 'brand' : project.viewType === 'sub-brand' ? 'sub-brand' : 'product'}: {group.groupName}
                          </span>
                        )}
                      </p>
                      <div className="space-y-1 text-xs">
                        {keptInstance && (
                          <div className="flex items-center gap-2 text-green-700">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>
                              <strong>Keep:</strong> {project.viewType === 'product' ? keptInstance.groupName : `in ${keptInstance.groupName}`}
                              {' '}(Vol: {keptInstance.volume}
                              {keptInstance.intentScore && `, Intent: ${keptInstance.intentScore.toFixed(2)}`})
                            </span>
                          </div>
                        )}
                        {removedInstances.map((inst, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-red-600 ml-6">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>
                              Remove: {project.viewType === 'product' ? inst.groupName : `in ${inst.groupName}`}
                              {' '}(Vol: {inst.volume}
                              {inst.intentScore && `, Intent: ${inst.intentScore.toFixed(2)}`})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              disabled={removedCount === 0}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export Duplicates Analysis</span>
            </button>
            
            {deduplicationState && (
              <button
                onClick={() => {
                  onRestoreDeduplication();
                  onClose();
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Restore All Duplicates</span>
              </button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={removedCount === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Deduplication
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeduplicationModal;
